import { type FC, useState } from 'react';

import { getFileSizeError } from '@/utils/validate-file-size';
import { isFileTypeAccepted } from '@/utils/validate-file-type';

import Dropzone from '../Dropzone';
import Helper from '../Helper';
import { FileItem } from './file-item';
import { StyledFileList, StyledRoot } from './FileUpload.styled';
import { FileItemFrame } from './item-frame';
import type { FileUploadProps } from './types';

const FileUpload: FC<FileUploadProps> = ({
  files = [],
  variant = 'file',
  onFilesSelected,
  onRemove,
  onDownload,
  accept,
  acceptLabel,
  hint,
  subject,
  maxSize,
  multiple = false,
  disabled = false,
  helperText,
  state = 'default',
}) => {
  const [localError, setLocalError] = useState<string | null>(null);

  const handleFilesSelected = (incoming: File[]) => {
    const candidates = multiple ? incoming : incoming.slice(0, 1);
    const valid: File[] = [];
    let firstError: string | null = null;

    candidates.forEach((file) => {
      if (!isFileTypeAccepted(file, accept)) {
        firstError ??= `"${file.name}" is not a supported file type.`;
        return;
      }

      const sizeError = maxSize ? getFileSizeError(file, maxSize) : null;
      if (sizeError) {
        firstError ??= sizeError;
        return;
      }

      valid.push(file);
    });

    setLocalError(firstError);
    if (valid.length > 0) onFilesSelected(valid);
  };

  const showDropzone = multiple || files.length === 0;
  const rows = files.map((file) => (
    <FileItem
      key={file.id}
      file={file}
      variant={variant}
      onRemove={onRemove}
      onDownload={onDownload}
    />
  ));

  return (
    <StyledRoot>
      {showDropzone && (
        <Dropzone
          onFilesSelected={handleFilesSelected}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          acceptLabel={acceptLabel}
          hint={hint}
          subject={subject}
          maxSize={maxSize}
          state={localError ? 'destructive' : state}
        />
      )}

      {(localError || helperText) && (
        <Helper state={localError ? 'destructive' : state} label={localError ?? helperText} />
      )}

      {files.length > 0 &&
        (showDropzone ? (
          <StyledFileList>{rows}</StyledFileList>
        ) : (
          <FileItemFrame>{rows}</FileItemFrame>
        ))}
    </StyledRoot>
  );
};

FileUpload.displayName = 'FileUpload';

export default FileUpload;
